'use client'

import { Event, EventTicket, EventTicketPackage, EventImage, EventPromoter, Profile, Inbox } from '../models';
import { InboxStateCategory } from './InboxContext'; 

// ====================================================================================
// --- TYPE DEFINITIONS: The Single Source of Truth for State and Actions ---
// ====================================================================================

// ---------------- //
// --- Events --- //
// ---------------- //
export interface EventsDataState {
    currentEventId: string | null;
    pendingEvent: Event | null;
    currentEvent: Event | null;
    events: Event[];
    myEventsCompleted: Event[];
    myEventsUpcoming: Event[];
    myEventsCancelled: Event[];
    myticketsCompleted: Event[];
    myticketsCancelled: Event[];
    myticketsUpcoming: Event[];
    myticketsRequests: Event[];
    myticketsApproved: Event[];
    loved: Event[];
}

export interface EventsFilterState {
    sort: string;
    byStock: boolean;
    byFastDelivery: boolean;
    byRating: number;
    searchQuery: string;
    statusQuery: string;
    ticketStatusQuery: string;
    ticketOrEventStatusQuery: { event: string; ticket: string; operator: 'OR' };
    ticketAndEventStatusQuery: { event: string; ticket: string; operator: 'AND' };
}

const VALID_FILTER_ACTION_TYPES = ["SORT_BY_PRICE", "FILTER_BY_STOCK", "FILTER_BY_DELIVERY", "FILTER_BY_RATING", "FILTER_BY_SEARCH", "FILTER_BY_STATUS", "FILTER_BY_TICKET_STATUS", "FILTER_BY_EVENT_OR_TICKET_STATUS", "FILTER_BY_EVENT_AND_TICKET_STATUS", "CLEAR_FILTERS"] as const;
export type FilterActionType = typeof VALID_FILTER_ACTION_TYPES[number];
export function isValidFilterActionType(value: any): value is FilterActionType { return VALID_FILTER_ACTION_TYPES.includes(value); }

export type EventsAction =
  | { type: 'INITIALIZE_STATE'; payload: Partial<EventsDataState> }
  | { type: 'RESET_STATE' }
  | { type: 'SET_CURRENT_EVENT_ID'; payload: string | null }
  | { type: 'SET_CURRENT_EVENT'; payload: { event: Event } }
  | { type: 'SET_PENDING_EVENT'; payload: { event: Event | null } }
  | { type: 'UPDATE_PENDING_EVENT'; payload: { event: Partial<Event> } }
  | { type: 'REPLACE_EVENTS'; payload: { events: Event[] } }
  | { type: 'SET_EVENTS'; payload: { events: Event[] } }
  | { type: 'SET_MY_UPCOMING_EVENTS'; payload: { events: Event[] } }
  | { type: 'SET_MY_COMPLETED_EVENTS'; payload: { events: Event[] } }
  | { type: 'SET_MY_CANCELLED_EVENTS'; payload: { events: Event[] } }
  | { type: 'SET_MY_UPCOMING_TICKETS'; payload: { events: Event[] } }
  | { type: 'SET_MY_COMPLETED_TICKETS'; payload: { events: Event[] } }
  | { type: 'SET_MY_CANCELLED_TICKETS'; payload: { events: Event[] } }
  | { type: 'SET_MY_REQUESTS_TICKETS'; payload: { events: Event[] } }
  | { type: 'SET_MY_APPROVED_TICKETS'; payload: { events: Event[] } }
  | { type: 'UPDATE_EVENT'; payload: { event: Event } }
  | { type: 'CANCEL_EVENT'; payload: { eventId: string } }
  | { type: 'UPDATE_TICKET'; payload: { eventId: string; ticket: Partial<EventTicket> & { id: string } } }
  | { type: 'ADD_EVENT_PRICE_PACKAGE'; payload: { id: string, pkg: EventTicketPackage } }
  | { type: 'UPDATE_EVENT_PRICE_PACKAGE'; payload: { id: string, pkg: EventTicketPackage } }
  | { type: 'DELETE_EVENT_PRICE_PACKAGE'; payload: { id: string, packageId: string } }
  | { type: 'ADD_EVENT_PICTURES'; payload: { id: string, images: EventImage[] } }
  | { type: 'DELETE_EVENT_PICTURE'; payload: { id: string, imageId: string } }
  | { type: 'ADD_EVENT_PROMOTER'; payload: { id: string, promoter: EventPromoter } }
  | { type: 'UPDATE_EVENT_PROMOTER'; payload: { id: string, promoter: EventPromoter } }
  | { type: 'DELETE_EVENT_PROMOTER'; payload: { id: string, promoterId: string } }
  | { type: 'CLEAR_USER_DATA' } ;

export type FilterAction = 
  | { type: 'INITIALIZE_FILTERS'; payload: Partial<EventsFilterState> }
  | { type: 'RESET_FILTERS' }
  | { type: FilterActionType; payload: any };

// ---------------- //
// --- People --- //
// ---------------- //
export interface PeopleDataState {
    socialState: 'friends' | 'requests' | 'requestsSent' | 'discovery';
    friendsState: Profile[];
    requestsState: Profile[];
    requestsSentState: Profile[];
    friendsDiscoveryState: Profile[];
}

export interface PeopleFilterState {
    searchQuery: string;
    sort: string;
    byRating: number;
    statusQuery: string;
}

export type PeopleAction =
  | { type: 'INITIALIZE_SOCIAL_STATE'; payload: Partial<PeopleDataState> }
  | { type: 'RESET_STATE' }
  | { type: 'SET_SOCIAL_STATE'; payload: PeopleDataState['socialState'] }
  // --- MERGE ACTIONS (for infinite scroll) ---
  | { type: 'SET_FRIENDS_STATE'; payload: Profile[] }
  | { type: 'SET_REQUESTS_STATE'; payload: Profile[] }
  | { type: 'SET_REQUESTS_SENT_STATE'; payload: Profile[] }
  | { type: 'SET_FRIEND_DISCOVERY_STATE'; payload: Profile[] }
  // --- REPLACE ACTIONS (for tab switching/refreshing) ---
  | { type: 'REPLACE_FRIENDS_STATE'; payload: Profile[] }
  | { type: 'REPLACE_REQUESTS_STATE'; payload: Profile[] }
  | { type: 'REPLACE_REQUESTS_SENT_STATE'; payload: Profile[] }
  | { type: 'REPLACE_FRIEND_DISCOVERY_STATE'; payload: Profile[] }
  // --- INDIVIDUAL ITEM ACTIONS ---
  | { type: 'ADD_FRIEND'; payload: { friend: Profile } }
  | { type: 'REMOVE_FRIEND'; payload: { id: string } }
  | { type: 'ADD_REQUEST_SENT'; payload: { friend: Profile } }
  | { type: 'REMOVE_REQUEST'; payload: { id: string } };

export type PeopleFilterAction = 
  | { type: 'INITIALIZE_FILTERS'; payload: Partial<PeopleFilterState> }
  | { type: 'RESET_FILTERS' };

// ---------------- //
// --- Inbox --- //
// ---------------- //
export interface InboxDataState {
    inboxState: InboxStateCategory;
    // privateInboxState: Inbox[] | null;
    // eventsInboxState: Inbox[] | null;
    supportUserInboxState: Inbox[] | null;
}

export interface InboxFilterState {
    sort: string;
    searchQuery: string;
}

export type InboxAction =
  | { type: 'INITIALIZE_INBOX_STATE'; payload: Partial<InboxDataState> }
  | { type: 'RESET_STATE' }
  | { type: 'SET_INBOX_STATE'; payload: InboxStateCategory }
//   | { type: 'SET_PRIVATE_INBOXES'; payload: { inboxes: Inbox[] } }
//   | { type: 'SET_EVENTS_INBOXES'; payload: { inboxes: Inbox[] } }
  | { type: 'SET_SUPPORT_USER_INBOXES'; payload: { inboxes: Inbox[] } }
  | { type: 'REPLACE_INBOX_LIST'; payload: { category: InboxStateCategory; inboxes: Inbox[] } };


// ====================================================================================
// --- DEFAULT STATES (Exported for use in contexts) ---
// ====================================================================================

export const defaultAllEventsState: EventsDataState = { currentEventId: null, pendingEvent: null, currentEvent: null, events: [], myEventsCompleted: [], myEventsUpcoming: [], myEventsCancelled: [], myticketsCompleted: [], myticketsCancelled: [], myticketsUpcoming: [], myticketsRequests: [], myticketsApproved: [], loved: [] };
export const defaultEventsFilterState: EventsFilterState = { sort: 'soonToLater', byStock: false, byFastDelivery: false, byRating: 0, searchQuery: "", statusQuery: "upcoming", ticketStatusQuery: "active", ticketOrEventStatusQuery: { event: "", ticket: "", operator: 'OR' }, ticketAndEventStatusQuery: { "event": "upcoming", "ticket": "active", 'operator': 'AND' } };
export const defaultPeopleState: PeopleDataState = { socialState: 'friends', friendsState: [], requestsState: [], requestsSentState: [], friendsDiscoveryState: [] };
export const defaultPeopleFilterState: PeopleFilterState = { searchQuery: "", sort: 'name_asc', byRating: 0, statusQuery: "friends" };
export const defaultInboxState: InboxDataState = { inboxState: 'supportUser', supportUserInboxState: null };
export const defaultInboxFilterState: InboxFilterState = { sort: 'soonToLater', searchQuery: "" };


// ====================================================================================
// --- HELPER FUNCTIONS (Typed, Immutable, and Reusable) ---
// ====================================================================================

const mergeAndGetUnique = <T extends { id: any }>(existing: T[] | null, newItems: T[]): T[] => {
    const combined = [...(existing || []), ...newItems];
    const uniqueMap = new Map(combined.map(item => [item.id, item]));
    return Array.from(uniqueMap.values());
};

const updateItemInArray = <T extends { id: any }>(array: T[], updatedItem: Partial<T> & { id: any }): T[] => {
    return array.map(item => item.id === updatedItem.id ? { ...item, ...updatedItem } : item);
};

const removeItemFromArray = <T extends { id: any }>(array: T[], itemId: any): T[] => {
    return array.filter(item => item.id !== itemId);
};

const updateEventInAllLists = (state: EventsDataState, eventId: string, updateFn: (event: Event) => Event): EventsDataState => {
    const updater = (event: Event) => (event.id === eventId ? updateFn(event) : event);
    const newState: EventsDataState = { ...state };
    if (newState.currentEvent?.id === eventId) newState.currentEvent = updateFn(newState.currentEvent);
    if (newState.events.some(e => e.id === eventId)) newState.events = newState.events.map(updater);
    if (newState.myEventsUpcoming.some(e => e.id === eventId)) newState.myEventsUpcoming = newState.myEventsUpcoming.map(updater);
    if (newState.myticketsUpcoming.some(e => e.id === eventId)) newState.myticketsUpcoming = newState.myticketsUpcoming.map(updater);
    return newState;
};


// ====================================================================================
// --- REDUCERS (The Core State Logic) ---
// ====================================================================================

export const eventsReducer = (state: EventsDataState, action: EventsAction): EventsDataState => {
    switch (action.type) {
        case 'INITIALIZE_STATE': return { ...state, ...action.payload };
        case 'RESET_STATE': return defaultAllEventsState;
        case 'SET_CURRENT_EVENT_ID': return { ...state, currentEventId: action.payload };
        case 'SET_CURRENT_EVENT': return { ...state, currentEvent: action.payload.event };
        case 'SET_PENDING_EVENT': return { ...state, pendingEvent: action.payload.event };
        case 'UPDATE_PENDING_EVENT':
            if (!state.pendingEvent) return state;
            return { ...state, pendingEvent: { ...state.pendingEvent, ...action.payload.event } };
        case 'REPLACE_EVENTS': return { ...state, events: action.payload.events };
        case 'SET_EVENTS': return { ...state, events: mergeAndGetUnique(state.events, action.payload.events) };
        case 'SET_MY_UPCOMING_EVENTS': return { ...state, myEventsUpcoming: mergeAndGetUnique(state.myEventsUpcoming, action.payload.events) };
        case 'SET_MY_COMPLETED_EVENTS': return { ...state, myEventsCompleted: mergeAndGetUnique(state.myEventsCompleted, action.payload.events) };
        case 'SET_MY_CANCELLED_EVENTS': return { ...state, myEventsCancelled: mergeAndGetUnique(state.myEventsCancelled, action.payload.events) };
        case 'SET_MY_UPCOMING_TICKETS': return { ...state, myticketsUpcoming: mergeAndGetUnique(state.myticketsUpcoming, action.payload.events) };
        case 'SET_MY_COMPLETED_TICKETS': return { ...state, myticketsCompleted: mergeAndGetUnique(state.myticketsCompleted, action.payload.events) };
        case 'SET_MY_CANCELLED_TICKETS': return { ...state, myticketsCancelled: mergeAndGetUnique(state.myticketsCancelled, action.payload.events) };
        case 'SET_MY_REQUESTS_TICKETS': return { ...state, myticketsRequests: mergeAndGetUnique(state.myticketsRequests, action.payload.events) };
        case 'SET_MY_APPROVED_TICKETS': return { ...state, myticketsApproved: mergeAndGetUnique(state.myticketsApproved, action.payload.events) };
        
        case 'CLEAR_USER_DATA':
            // This is our new "soft" reset for logging out.
            // It spreads the existing state, which preserves the public `events` array,
            // but then explicitly clears out all the data that is specific to a logged-in user.
            return {
                ...state, // Keep the current state (including the public `events` list)
                
                // Now, clear all user-specific data
                currentEventId: null,
                pendingEvent: null,
                currentEvent: null, // Clear current event if it might be user-specific
                myEventsCompleted: [],
                myEventsUpcoming: [],
                myEventsCancelled: [],
                myticketsCompleted: [],
                myticketsCancelled: [],
                myticketsUpcoming: [],
                myticketsRequests: [],
                myticketsApproved: [],
                loved: [],
            };
        default: return state;
    }
};

export const eventsFilterReducer = (state: EventsFilterState, action: FilterAction): EventsFilterState => {
    switch (action.type) {
        case 'INITIALIZE_FILTERS': return { ...state, ...action.payload };
        case 'RESET_FILTERS': return defaultEventsFilterState;
        case "SORT_BY_PRICE": return { ...state, sort: action.payload };
        case "FILTER_BY_STOCK": return { ...state, byStock: !state.byStock };
        case "FILTER_BY_DELIVERY": return { ...state, byFastDelivery: !state.byFastDelivery };
        case "FILTER_BY_RATING": return { ...state, byRating: action.payload };
        case "FILTER_BY_SEARCH": return { ...state, searchQuery: action.payload };
        case "FILTER_BY_STATUS": return { ...state, statusQuery: action.payload };
        case "FILTER_BY_TICKET_STATUS": return { ...state, ticketStatusQuery: action.payload };
        case "FILTER_BY_EVENT_OR_TICKET_STATUS": return { ...state, ticketOrEventStatusQuery: action.payload };
        case "FILTER_BY_EVENT_AND_TICKET_STATUS": return { ...state, ticketAndEventStatusQuery: action.payload };
        case "CLEAR_FILTERS": return { ...state, byStock: false, byFastDelivery: false, byRating: 0, searchQuery: '' };
        default: return state;
    }
};

export const peopleReducer = (state: PeopleDataState, action: PeopleAction): PeopleDataState => {
    switch (action.type) {
        case 'INITIALIZE_SOCIAL_STATE': return { ...state, ...action.payload };
        case 'RESET_STATE': return defaultPeopleState;
        case 'SET_SOCIAL_STATE': return { ...state, socialState: action.payload };

        // --- MERGE ACTIONS (for infinite scroll 'load more') ---
        case 'SET_FRIENDS_STATE': 
            return { ...state, friendsState: mergeAndGetUnique(state.friendsState, action.payload) };
        case 'SET_REQUESTS_STATE': 
            return { ...state, requestsState: mergeAndGetUnique(state.requestsState, action.payload) };
        case 'SET_REQUESTS_SENT_STATE': 
            return { ...state, requestsSentState: mergeAndGetUnique(state.requestsSentState, action.payload) };
        case 'SET_FRIEND_DISCOVERY_STATE': 
            return { ...state, friendsDiscoveryState: mergeAndGetUnique(state.friendsDiscoveryState, action.payload) };

        // --- REPLACE ACTIONS (for refreshing a tab) ---
        case 'REPLACE_FRIENDS_STATE':
            return { ...state, friendsState: action.payload };
        case 'REPLACE_REQUESTS_STATE':
            return { ...state, requestsState: action.payload };
        case 'REPLACE_REQUESTS_SENT_STATE':
            return { ...state, requestsSentState: action.payload };
        case 'REPLACE_FRIEND_DISCOVERY_STATE':
            return { ...state, friendsDiscoveryState: action.payload };

        // --- INDIVIDUAL ITEM ACTIONS ---
        case 'ADD_FRIEND': 
            return { ...state, friendsState: mergeAndGetUnique(state.friendsState, [action.payload.friend]) };
        case 'REMOVE_FRIEND': 
            return { ...state, friendsState: state.friendsState.filter(friend => friend.id !== action.payload.id) };
        case 'ADD_REQUEST_SENT': 
            return { ...state, requestsSentState: mergeAndGetUnique(state.requestsSentState, [action.payload.friend]) };
        case 'REMOVE_REQUEST': 
            return { ...state, requestsState: state.requestsState.filter(req => req.id !== action.payload.id) };
            
        default: return state;
    }
};

export const peopleFilterReducer = (state: PeopleFilterState, action: PeopleFilterAction): PeopleFilterState => {
    switch (action.type) {
        case 'INITIALIZE_FILTERS': return { ...state, ...action.payload };
        case 'RESET_FILTERS': return defaultPeopleFilterState;
        default: return state;
    }
};

export const inboxReducer = (state: InboxDataState, action: InboxAction): InboxDataState => {
    switch (action.type) {
        case 'INITIALIZE_INBOX_STATE': return { ...state, ...action.payload };
        case 'RESET_STATE': return defaultInboxState;
        case 'SET_INBOX_STATE': return { ...state, inboxState: action.payload };
        // case 'SET_PRIVATE_INBOXES': return { ...state, privateInboxState: mergeAndGetUnique(state.privateInboxState, action.payload.inboxes) };
        // case 'SET_EVENTS_INBOXES': return { ...state, eventsInboxState: mergeAndGetUnique(state.eventsInboxState, action.payload.inboxes) };
        case 'SET_SUPPORT_USER_INBOXES': return { ...state, supportUserInboxState: mergeAndGetUnique(state.supportUserInboxState, action.payload.inboxes) };
        case 'REPLACE_INBOX_LIST': 
            {
                const { category, inboxes } = action.payload;
                const stateKeyMap = {
                    // private: 'privateInboxState',
                    // event: 'eventsInboxState', 
                    supportUser: 'supportUserInboxState'
                };
                const stateKey = stateKeyMap[category];
                return { ...state, [stateKey]: inboxes };
            }
        default: return state;
    }
};

export const inboxFilterReducer = (state: InboxFilterState, action: any): InboxFilterState => {
    switch (action.type) {
        case 'INITIALIZE_FILTERS': return { ...state, ...action.payload };
        case 'RESET_FILTERS': return defaultInboxFilterState;
        // Add other filter cases like 'SET_SORT' or 'SET_SEARCH_QUERY' here
        default: return state;
    }
};

